// common.js: shared helpers for world modules. READ-ONLY for world authors
// (import what you need; if something is missing, copy it into your world).
import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

export { FullScreenQuad };

/* ---------------- math ---------------- */
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const lerp = (a, b, t) => a + (b - a) * t;
export const smooth = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
// progress within [a, b] as 0..1 (clamped); use it to split your chapter into beats
export const seg = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
// frame-rate independent exponential smoothing
export const damp = (cur, target, lambda, dt) => cur + (target - cur) * (1 - Math.exp(-lambda * dt));
export const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
export const easeOut = (t) => 1 - Math.pow(1 - t, 3);
// deterministic RNG (mulberry32)
export function rng(seed = 1) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---------------- colour ---------------- */
// B-V colour index -> effective temperature (Ballesteros 2012)
export function bvToTemp(bv) {
  bv = clamp(bv, -0.4, 2.0);
  return 4600 * (1 / (0.92 * bv + 1.7) + 1 / (0.92 * bv + 0.62));
}
// Blackbody temperature (K) -> LINEAR sRGB, normalised so max channel = 1.
// Planckian locus fit (Kim et al. 2002) -> XYZ -> linear sRGB.
export function tempToRGB(T) {
  T = clamp(T, 1667, 25000);
  const t = 1 / T, t2 = t * t, t3 = t2 * t;
  const x = T < 4000 ? -0.2661239e9 * t3 - 0.2343589e6 * t2 + 0.8776956e3 * t + 0.17991
    : -3.0258469e9 * t3 + 2.1070379e6 * t2 + 0.2226347e3 * t + 0.24039;
  const x2 = x * x, x3 = x2 * x;
  const y = T < 2222 ? -1.1063814 * x3 - 1.3481102 * x2 + 2.18555832 * x - 0.20219683
    : T < 4000 ? -0.9549476 * x3 - 1.37418593 * x2 + 2.09137015 * x - 0.16748867
      : 3.081758 * x3 - 5.8733867 * x2 + 3.75112997 * x - 0.37001483;
  const X = x / y, Y = 1, Z = (1 - x - y) / y;
  let r = 3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z;
  let g = -0.969266 * X + 1.8760108 * Y + 0.041556 * Z;
  let b = 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z;
  r = Math.max(r, 0); g = Math.max(g, 0); b = Math.max(b, 0);
  const m = Math.max(r, g, b) || 1;
  return [r / m, g / m, b / m];
}
// brand colours as LINEAR rgb (for shaders / materials)
const s2l = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const hex = (h) => [1, 3, 5].map((i) => s2l(parseInt(h.slice(i, i + 2), 16) / 255));
export const BRAND = {
  yellow: hex('#FDDE00'), amber: hex('#FFB23F'), orange: hex('#F34747'),
  violet: hex('#7B5CFF'), ink: hex('#EFEDE8'), night: hex('#05060C'),
};

/* ---------------- GPU helpers ---------------- */
export function makeTarget(w, h, { half = true, depth = false, linear = true, samples = 0 } = {}) {
  return new THREE.WebGLRenderTarget(Math.max(1, w | 0), Math.max(1, h | 0), {
    type: half ? THREE.HalfFloatType : THREE.UnsignedByteType,
    depthBuffer: depth,
    samples,
    minFilter: linear ? THREE.LinearFilter : THREE.NearestFilter,
    magFilter: linear ? THREE.LinearFilter : THREE.NearestFilter,
  });
}

const CATMULL = /* glsl */`
vec3 sampleBicubic(sampler2D tex, vec2 uv, vec2 texel) {
  vec2 sp = uv / texel;
  vec2 t1 = floor(sp - 0.5) + 0.5;
  vec2 f = sp - t1;
  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
  vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
  vec2 w3 = f * f * (-0.5 + 0.5 * f);
  vec2 w12 = w1 + w2;
  vec2 o12 = w2 / w12;
  vec2 p0 = (t1 - 1.0) * texel, p3 = (t1 + 2.0) * texel, p12 = (t1 + o12) * texel;
  vec3 r = vec3(0.0);
  r += texture2D(tex, vec2(p0.x, p0.y)).rgb * w0.x * w0.y;
  r += texture2D(tex, vec2(p12.x, p0.y)).rgb * w12.x * w0.y;
  r += texture2D(tex, vec2(p3.x, p0.y)).rgb * w3.x * w0.y;
  r += texture2D(tex, vec2(p0.x, p12.y)).rgb * w0.x * w12.y;
  r += texture2D(tex, vec2(p12.x, p12.y)).rgb * w12.x * w12.y;
  r += texture2D(tex, vec2(p3.x, p12.y)).rgb * w3.x * w12.y;
  r += texture2D(tex, vec2(p0.x, p3.y)).rgb * w0.x * w3.y;
  r += texture2D(tex, vec2(p12.x, p3.y)).rgb * w12.x * w3.y;
  r += texture2D(tex, vec2(p3.x, p3.y)).rgb * w3.x * w3.y;
  return max(r, 0.0);
}`;

// Copy/upsample a texture into a target (e.g. a half-res raymarch into the
// full-res HDR target). bicubic = sharper upsampling (Catmull-Rom, 9 taps).
// blending: THREE.NoBlending (replace) or THREE.AdditiveBlending (add on top).
export class Blit {
  constructor({ bicubic = false, blending = THREE.NoBlending } = {}) {
    this.q = new FullScreenQuad(new THREE.ShaderMaterial({
      uniforms: { map: { value: null }, gain: { value: 1 }, texel: { value: new THREE.Vector2(1, 1) } },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: `uniform sampler2D map; uniform float gain; uniform vec2 texel; varying vec2 vUv;
        ${bicubic ? CATMULL : ''}
        void main(){ gl_FragColor = vec4(${bicubic ? 'sampleBicubic(map, vUv, texel)' : 'texture2D(map, vUv).rgb'} * gain, 1.0); }`,
      depthTest: false, depthWrite: false, blending, transparent: blending !== THREE.NoBlending,
    }));
  }
  // source: a WebGLRenderTarget (or { texture, width, height }); target: WebGLRenderTarget or null
  render(renderer, source, target, gain = 1) {
    const u = this.q.material.uniforms;
    u.map.value = source.texture || source;
    u.gain.value = gain;
    if (source.width) u.texel.value.set(1 / source.width, 1 / source.height);
    renderer.setRenderTarget(target);
    this.q.render(renderer);
  }
  dispose() { this.q.material.dispose(); this.q.dispose(); }
}

/* ---------------- loading ---------------- */
const texLoader = new THREE.TextureLoader();
// colour images: srgb = true (hardware-decoded to linear when sampled); data maps: srgb = false
export function loadTexture(url, { srgb = true, repeat = false, mipmaps = true, anisotropy = 8, renderer = null, flipY = true } = {}) {
  return new Promise((resolve, reject) => texLoader.load(url, (t) => {
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.flipY = flipY;
    t.generateMipmaps = mipmaps;
    t.minFilter = mipmaps ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    if (renderer) t.anisotropy = Math.min(anisotropy, renderer.capabilities.getMaxAnisotropy());
    t.needsUpdate = true;
    resolve(t);
  }, undefined, () => reject(new Error(`load ${url}`))));
}
export async function loadBinary(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url} ${r.status}`);
  return r.arrayBuffer();
}
export async function loadJSON(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url} ${r.status}`);
  return r.json();
}

/* ---------------- overlay anchors ---------------- */
const _v = new THREE.Vector3();
// World position -> overlay anchor. x, y in 0..1 from the TOP-LEFT of the
// canvas (CSS convention). visible = in front of the camera and on screen.
export function anchor(camera, pos, out = {}) {
  _v.copy(pos).applyMatrix4(camera.matrixWorldInverse);
  const inFront = _v.z < 0;
  _v.applyMatrix4(camera.projectionMatrix);
  out.x = _v.x * 0.5 + 0.5;
  out.y = 0.5 - _v.y * 0.5;
  out.visible = inFront && out.x > -0.02 && out.x < 1.02 && out.y > -0.02 && out.y < 1.02;
  return out;
}

/* ---------------- GLSL chunks ---------------- */
// Hash without Sine (Dave Hoskins, MIT)
export const GLSL_HASH = /* glsl */`
float hash11(float p) { p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float hash13(vec3 p3) { p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
vec2 hash22(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.xx + p3.yz) * p3.zy); }
vec3 hash33(vec3 p3) { p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yxz + 33.33); return fract((p3.xxy + p3.yxx) * p3.zyx); }
`;

// 3D simplex noise (Ashima Arts / Stefan Gustavson, MIT) + fbm
export const GLSL_NOISE = /* glsl */`
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.5 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 105.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}
float fbm3(vec3 p, int oct) {
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 8; i++) {
    if (i >= oct) break;
    s += a * snoise(p);
    p = p * 2.02 + vec3(17.1, 3.7, 9.2);
    a *= 0.5;
  }
  return s;
}
`;

// Blackbody temperature (K) -> LINEAR sRGB, max channel = 1 (same fit as tempToRGB)
export const GLSL_BLACKBODY = /* glsl */`
vec3 blackbody(float T) {
  T = clamp(T, 1667.0, 25000.0);
  float t = 1.0 / T, t2 = t * t, t3 = t2 * t;
  float x = T < 4000.0 ? (-0.2661239e9 * t3 - 0.2343589e6 * t2 + 0.8776956e3 * t + 0.179910)
                       : (-3.0258469e9 * t3 + 2.1070379e6 * t2 + 0.2226347e3 * t + 0.240390);
  float x2 = x * x, x3 = x2 * x;
  float y = T < 2222.0 ? (-1.1063814 * x3 - 1.34811020 * x2 + 2.18555832 * x - 0.20219683)
          : T < 4000.0 ? (-0.9549476 * x3 - 1.37418593 * x2 + 2.09137015 * x - 0.16748867)
                       : ( 3.0817580 * x3 - 5.87338670 * x2 + 3.75112997 * x - 0.37001483);
  vec3 XYZ = vec3(x / y, 1.0, (1.0 - x - y) / y);
  vec3 rgb = mat3(3.2404542, -0.9692660, 0.0556434,
                  -1.5371385, 1.8760108, -0.2040259,
                  -0.4985314, 0.0415560, 1.0572252) * XYZ;
  rgb = max(rgb, 0.0);
  return rgb / max(rgb.r, max(rgb.g, rgb.b));
}
`;
